import { useEffect, useRef, useState } from "react";
import { api, apiErrorText, API, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FilePdf, FileText, Image as ImageIcon, DownloadSimple, Trash, PaperPlaneTilt } from "@phosphor-icons/react";

const CATEGORIES = {
  proposal: { label: "Proposta", cls: "bg-accent/20 text-accent" },
  contract: { label: "Contrato", cls: "bg-primary/20 text-primary" },
  invoice: { label: "Fatura", cls: "bg-amber-500/20 text-amber-400" },
  other: { label: "Outro", cls: "bg-muted text-muted-foreground" },
};

const STATUS = {
  draft: { label: "Rascunho", cls: "bg-muted text-muted-foreground" },
  sent: { label: "Enviado", cls: "bg-accent/20 text-accent" },
  approved: { label: "Aprovado", cls: "bg-green-500/20 text-green-400" },
  signed: { label: "Assinado", cls: "bg-primary/20 text-primary" },
  rejected: { label: "Recusado", cls: "bg-destructive/20 text-destructive" },
};

function fileIcon(contentType) {
  if (!contentType) return FileText;
  if (contentType.includes("pdf")) return FilePdf;
  if (contentType.startsWith("image/")) return ImageIcon;
  return FileText;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function Attachments({ clientId, category, title = "Anexos" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({
    file: null, title: "", category: category || "other", status: "draft", notes: "",
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = { client_id: clientId };
      if (category) params.category = category;
      const { data } = await api.get("/attachments", { params });
      setRows(data);
    } catch (e) { toast.error(apiErrorText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId, category]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Arquivo excede 15MB"); return; }
    setForm((s) => ({ ...s, file: f, title: s.title || f.name.replace(/\.[^.]+$/, "") }));
  };

  const submitUpload = async () => {
    if (!form.file) { toast.error("Selecione um arquivo"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("client_id", clientId);
      fd.append("category", form.category);
      fd.append("title", form.title);
      fd.append("status", form.status);
      fd.append("notes", form.notes);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento anexado");
      setUploadOpen(false);
      setForm({ file: null, title: "", category: category || "other", status: "draft", notes: "" });
      load();
    } catch (e) { toast.error(apiErrorText(e)); }
    finally { setUploading(false); }
  };

  const updateStatus = async (aid, status) => {
    try {
      await api.patch(`/attachments/${aid}`, { status });
      toast.success("Status atualizado");
      load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const markSent = async (aid) => updateStatus(aid, "sent");

  const remove = async (aid) => {
    if (!window.confirm("Remover este anexo?")) return;
    try {
      await api.delete(`/attachments/${aid}`);
      toast.success("Anexo removido");
      load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const downloadBlob = async (aid, filename) => {
    try {
      const resp = await api.get(`/attachments/${aid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "anexo";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const openInline = async (aid) => {
    try {
      const resp = await api.get(`/attachments/${aid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-tech text-muted-foreground">{title}</div>
          <p className="text-sm text-muted-foreground mt-1">Anexe documentos gerados fora do ERP e controle o status de envio</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="gap-2" data-testid="upload-attachment-btn">
          <Upload size={16} weight="bold" /> Anexar documento
        </Button>
      </div>

      {loading ? (
        <div className="label-tech text-muted-foreground animate-pulse">Carregando...</div>
      ) : rows.length === 0 ? (
        <div className="tech-card p-12 text-center">
          <div className="label-tech text-muted-foreground mb-2">Nenhum documento anexado</div>
          <button onClick={() => setUploadOpen(true)} className="text-accent font-bold hover:underline">Anexar o primeiro →</button>
        </div>
      ) : (
        <div className="tech-card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="label-tech text-muted-foreground p-4">Arquivo</th>
                {!category && <th className="label-tech text-muted-foreground p-4">Categoria</th>}
                <th className="label-tech text-muted-foreground p-4">Status</th>
                <th className="label-tech text-muted-foreground p-4">Enviado em</th>
                <th className="label-tech text-muted-foreground p-4">Criado</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody data-testid="attachments-list">
              {rows.map((r) => {
                const Icon = fileIcon(r.content_type);
                return (
                  <tr key={r.id} className="border-b border-border hover:bg-secondary/30" data-testid={`attachment-${r.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Icon size={22} className="text-accent shrink-0" weight="bold" />
                        <div className="min-w-0">
                          <div className="font-bold truncate">{r.title || r.original_filename}</div>
                          <div className="text-xs text-muted-foreground truncate mono">
                            {r.original_filename} · {fmtSize(r.size)}
                          </div>
                        </div>
                      </div>
                    </td>
                    {!category && (
                      <td className="p-4">
                        <Badge className={CATEGORIES[r.category]?.cls || ""}>{CATEGORIES[r.category]?.label || r.category}</Badge>
                      </td>
                    )}
                    <td className="p-4">
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`status-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4 text-xs mono text-muted-foreground">{r.sent_at ? formatDate(r.sent_at) : "—"}</td>
                    <td className="p-4 text-xs mono text-muted-foreground">{formatDate(r.created_at)}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        {r.status === "draft" && (
                          <Button size="icon" variant="ghost" onClick={() => markSent(r.id)} title="Marcar como enviado" data-testid={`send-${r.id}`}>
                            <PaperPlaneTilt size={16} className="text-accent" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => openInline(r.id)} title="Visualizar">
                          <FilePdf size={16} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => downloadBlob(r.id, r.original_filename)} title="Baixar" data-testid={`download-${r.id}`}>
                          <DownloadSimple size={16} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(r.id)} title="Remover">
                          <Trash size={16} className="text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Anexar documento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="label-tech">Arquivo * (PDF, DOCX, imagens — até 15MB)</Label>
              <input ref={fileRef} type="file" onChange={onFile} className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt" data-testid="attachment-file-input" />
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="gap-2">
                  <Upload size={14} weight="bold" /> Escolher arquivo
                </Button>
                <div className="text-sm text-muted-foreground truncate">
                  {form.file ? `${form.file.name} (${fmtSize(form.file.size)})` : "Nenhum arquivo selecionado"}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Proposta Comercial Torre 01" data-testid="attachment-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {!category && (
                <div className="space-y-2">
                  <Label className="label-tech">Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="attachment-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="label-tech">Status inicial</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancelar</Button>
            <Button onClick={submitUpload} disabled={uploading} data-testid="submit-upload-btn">
              {uploading ? "Enviando..." : "Anexar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
